<template>
  <ModalCard
    v-if="isOpened"
    :title="`${capitalize(modelName)} Restoration`"
    :handle-close="() => closeModal()"
    width="80%"
    height="95%"
  >
    <template #body-header>
      <section
        v-if="!isLoadingRestorable && Object.keys(errors).length > 0"
        class="notifications"
      >
        <div
          v-if="'no-restorables' in errors"
          class="notification is-danger p-2 mb-2"
        >
          <span class="icon">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          <span class="text has-text-weight-semibold">
            {{ errors['no-restorables'] }}
          </span>
        </div>
      </section>
    </template>
    <template #body>
      <section
        v-if="isLoadingRestorable"
        class="is-flex is-justify-content-center is-align-items-center"
        style="font-size: 22px; width: 100%; height: 100%"
      >
        <span class="icon is-large">
          <i class="fa fa-spinner fa-spin" />
        </span>
        Searching {{ pluralize('archive', isArchiveModel ? 1 : 2) }}
      </section>
      <section v-else>
        <DefaultPanel :is-collapsible="false" class="mb-5">
          <template #header>Information</template>
          <div>
            <div class="p-2 mb-3">
              <p class="mb-2">
                <!-- archive | datasets info  -->
                <span v-if="isArchiveModel">
                  <b>{{ datasetCount }}</b>
                  {{ pluralize('dataset', datasetCount) }} to be recovered from
                  long-term storage have been found in this archive.
                </span>
                <span v-else>
                  <span
                    class="tag is-rounded is-info has-text-weight-bold mr-1"
                  >
                    {{ datasetCount }}
                  </span>
                  {{ pluralize('dataset', datasetCount) }} have been selected to
                  be recovered from long term storage.
                </span>
              </p>
              <p v-if="nonRestorableDatasetsCount >= 1" class="mb-2">
                To find out why some datasets cannot be recovered, please
                consult the
                <b class="label is-inline">non-restorable datasets</b>
                list below
              </p>
            </div>

            <DefaultPanel
              class="mb-2"
              :collapsed="true"
              :empty="restorableDatasetsCount === 0"
            >
              <template #header>
                <span
                  class="tag is-rounded has-text-weight-bold"
                  :class="{ 'is-primary': restorableDatasetsCount >= 1 }"
                >
                  {{ restorableDatasetsCount }}
                </span>
                Restorable
                {{ pluralize('dataset', restorableDatasetsCount) }}
                <span class="mx-2 ml-4">
                  <span
                    v-for="info of restorabilityInfo"
                    :key="info.id"
                    v-tooltip="info.message"
                    class="icon is-size-6 is-small fa-stack hoverable-icon fa-sm mx-2"
                    :class="`has-text-${info.color}`"
                  >
                    <i
                      class="fa-solid stack-bg"
                      :class="{
                        'fa-hard-drive':
                          info.id === 'storage-online' ||
                          info.id === 'storage-access',
                        'fa-archive': info.id === 'archive-expiry',
                      }"
                    ></i>
                    <i
                      class="fa-solid stack-fg stack-small stack-bottom-right white-outline"
                      :class="{
                        'fa-signal': info.id === 'storage-online',
                        'fa-user': info.id === 'storage-access',
                        'fa-clock': info.id === 'archive-expiry',
                      }"
                    ></i>
                  </span>
                </span>
              </template>
              <div class="list-items-wrapper" @click.stop>
                <div
                  v-if="
                    !datafilesBelongToUserGroup ||
                    someVolumesOffline ||
                    someVolumeAccessDenied
                  "
                  class="notification is-warning is-light m-0"
                >
                  <span class="icon is-small has-text-warning mr-4">
                    <i class="fa-solid fa-exclamation-triangle fa-sm" />
                  </span>
                  <span class="text has-text-left mr-1">
                    Some datafile copies cannot be restored
                    <u>at their original location</u>
                    because
                    <ol
                      class="restore-loc-list"
                      :class="{ 'show-numbers': showListNumbers }"
                    >
                      <li v-if="someVolumesOffline">
                        some storage volume are offline
                        <span
                          class="icon is-small has-text-warning fa-stack fa-xs"
                        >
                          <i class="fa-solid fa-hard-drive stack-bg"></i>
                          <i
                            class="fa-solid fa-signal stack-fg stack-small stack-bottom-right white-outline"
                          ></i>
                        </span>
                      </li>
                      <li v-if="someVolumeAccessDenied">
                        you do not have access to some storage volume
                        <span
                          class="icon is-small has-text-warning fa-stack fa-xs"
                        >
                          <i class="fa-solid fa-hard-drive stack-bg"></i>
                          <i
                            class="fa-solid fa-user stack-fg stack-small stack-bottom-right white-outline"
                          ></i>
                        </span>
                      </li>
                      <li v-if="!datafilesBelongToUserGroup">
                        you do not have permissions to some storage volumes
                      </li>
                    </ol>
                    A custom destination has to be provided.
                  </span>
                </div>
                <DynamicScroller
                  class="scroller"
                  :items="restorableDatasets"
                  :min-item-size="44"
                >
                  <template #default="{ item, index, active }">
                    <DynamicScrollerItem
                      :item="item"
                      :active="active"
                      :size-dependencies="[item.showDetails]"
                      :data-index="index"
                      class="dynam-item"
                      :class="{ even: index % 2 == 0 }"
                    >
                      <a
                        class="item-name p-2"
                        @click="item.showDetails = !item.showDetails"
                      >
                        <div class="is-flex is-align-items-center">
                          <span class="icon hoverable-icon is-small mr-2">
                            <i
                              v-if="!item.showDetails"
                              class="fa-solid fa-angle-right fa-2xs"
                            />
                            <i v-else class="fa-solid fa-angle-down fa-2xs" />
                          </span>
                          <div class="title is-size-6 my-0 is-clickable">
                            {{ item.name }}
                          </div>
                        </div>

                        <ul class="detail-icons is-flex">
                          <li
                            v-for="{ id, message, color } of [
                              {
                                id: 'storage-online',
                                ...(item.volumeOnline
                                  ? {
                                      message: MESSAGES.storageOnline.success,
                                      color: 'success',
                                    }
                                  : {
                                      message: MESSAGES.storageOnline.error,
                                      color: 'warning',
                                    }),
                              },
                              {
                                id: 'storage-access',
                                ...(item.volumeAccess
                                  ? {
                                      message: MESSAGES.storageAccess.success,
                                      color: 'success',
                                    }
                                  : {
                                      message: MESSAGES.storageAccess.error,
                                      color: 'warning',
                                    }),
                              },
                              {
                                id: 'archive-expiry',
                                ...(!item.isExpired
                                  ? {
                                      message: MESSAGES.archiveExpired.success,
                                      color: 'success',
                                    }
                                  : {
                                      message: MESSAGES.archiveExpired.error,
                                      color: 'warning',
                                    }),
                              },
                            ]"
                            :key="`icon-${id}`"
                            class="detail-icon"
                          >
                            <span
                              v-tooltip="message"
                              class="icon is-small fa-stack hoverable-icon fa-sm"
                              :class="`has-text-${color}`"
                            >
                              <i
                                class="fa-solid stack-bg"
                                :class="{
                                  'fa-hard-drive':
                                    id === 'storage-online' ||
                                    id === 'storage-access',
                                  'fa-archive': id === 'archive-expiry',
                                }"
                              ></i>
                              <i
                                class="fa-solid stack-fg stack-small stack-bottom-right white-outline"
                                :class="{
                                  'fa-signal': id === 'storage-online',
                                  'fa-user': id === 'storage-access',
                                  'fa-clock': id === 'archive-expiry',
                                }"
                              ></i>
                            </span>
                          </li>
                        </ul>
                      </a>
                      <ul
                        v-if="item.showDetails"
                        :key="`list${item.id}`"
                        class="item-details px-2 pb-3"
                      >
                        <li
                          v-for="{
                            datafile,
                            datafilecopy,
                            isArchived: copyIsArchived,
                            isRestorable: copyIsRestorable,
                            restorabilityMsg,
                          } of item.datafileIds.map((id) => datafilesData[id])"
                          :key="datafile.id"
                          class="detail m-2 p-1"
                        >
                          <router-link
                            class="item-name mr-2"
                            :to="{ path: `/${datafile.id}` }"
                            target="_blank"
                            rel="noopener"
                          >
                            <span class="icon mr-2">
                              <i class="fa-regular fa-file" />
                            </span>
                            {{ datafile.name }}
                          </router-link>
                          <span class="item-error-msg">
                            <span class="icon mr-2">
                              <i class="fa-solid fa-caret-right" />
                            </span>
                            <code
                              v-tooltip="{
                                content: datafilecopy.uri,
                                popperClass: ['is-wide'],
                              }"
                              :class="
                                copyIsArchived && copyIsRestorable
                                  ? 'has-text-primary'
                                  : 'has-text-danger'
                              "
                            >
                              {{
                                copyIsArchived && copyIsRestorable
                                  ? datafilecopy.uri
                                  : restorabilityMsg
                              }}
                            </code>
                          </span>
                        </li>
                      </ul>
                    </DynamicScrollerItem>
                  </template>
                </DynamicScroller>
              </div>
            </DefaultPanel>
            <DefaultPanel
              :collapsed="true"
              :empty="nonRestorableDatasetsCount === 0"
            >
              <template #header>
                <span
                  class="tag is-rounded has-text-weight-bold"
                  :class="{ 'is-warning': nonRestorableDatasetsCount > 0 }"
                >
                  {{ nonRestorableDatasetsCount }}
                </span>
                Non-restorable
                {{ pluralize('dataset', nonRestorableDatasetsCount) }}
              </template>
              <div class="list-items-wrapper field m-0 p-0" @click.stop>
                <DynamicScroller
                  class="scroller"
                  :items="nonRestorableDatasets"
                  :min-item-size="44"
                >
                  <template #default="{ item, index, active }">
                    <DynamicScrollerItem
                      :item="item"
                      :active="active"
                      :size-dependencies="[item.showDetails]"
                      :data-index="index"
                      class="dynam-item"
                      :class="{ even: index % 2 == 0 }"
                    >
                      <a
                        class="p-2"
                        @click.stop="item.showDetails = !item.showDetails"
                      >
                        <div class="is-flex is-align-items-center">
                          <span class="icon hoverable-icon mr-3">
                            <i
                              v-if="!item.showDetails"
                              class="fa-solid fa-angle-right fa-2xs"
                            />
                            <i v-else class="fa-solid fa-angle-down fa-2xs" />
                          </span>
                          <div class="title is-size-6 my-0 is-clickable">
                            {{ item.name }}
                          </div>
                        </div>

                        <div>
                          <span
                            v-tooltip="{
                              content: item.restorabilityHTMLMsg,
                              html: true,
                            }"
                            class="detail-icon icon has-text-danger mx-2 has-text-weight-bold"
                          >
                            <i class="fa-solid fa-triangle-exclamation" />
                          </span>
                        </div>
                      </a>
                      <ul
                        v-if="item.showDetails"
                        class="item-details px-2 pb-3"
                      >
                        <li
                          v-for="{
                            datafile,
                            datafilecopy,
                            isArchived: copyIsArchived,
                            isRestorable: copyIsRestorable,
                            restorabilityMsg,
                          } of item.datafileIds.map((id) => datafilesData[id])"
                          :key="datafile.id"
                          class="detail py-1"
                        >
                          <span
                            v-tooltip="{
                              content: datafile.name,
                              popperClass: ['is-wide'],
                            }"
                            class="item-name mr-2"
                          >
                            <span class="icon mr-2">
                              <i class="fa-regular fa-file" />
                            </span>
                            {{ datafile.name }}
                          </span>
                          <span class="item-error-msg">
                            <span class="icon mr-2">
                              <i class="fa-solid fa-caret-right" />
                            </span>
                            <code
                              v-tooltip="{
                                content: datafilecopy.uri,
                                popperClass: ['is-wide'],
                              }"
                              :class="
                                copyIsArchived && copyIsRestorable
                                  ? 'has-text-primary'
                                  : 'has-text-danger'
                              "
                            >
                              {{
                                copyIsArchived && copyIsRestorable
                                  ? datafilecopy.uri
                                  : restorabilityMsg
                              }}
                            </code>
                          </span>
                        </li>
                      </ul>
                    </DynamicScrollerItem>
                  </template>
                </DynamicScroller>
              </div>
            </DefaultPanel>
          </div>
        </DefaultPanel>

        <DefaultPanel :is-collapsible="false">
          <template #header>Restore Options</template>
          <div>
            <!-- restorable info -->

            <template v-if="restorableDatasetsCount > 0">
              <p class="mb-5">
                Datafile copies are by default recovered
                <u>
                  at their original location
                  <span
                    v-tooltip="{
                      content: `The original location each datafile copy can be individually consulted in the
                      <code>Restorable datasets</code> list above.`,
                      html: true,
                    }"
                    class="icon hoverable-icon has-text-info"
                  >
                    <i class="fa fa-info-circle"></i>
                  </span>
                </u>
                Please provide a custom destination if needed.
              </p>
            </template>

            <div class="field is-horizontal">
              <div class="field-label has-text-left">
                <label class="label">Restore Destination</label>
              </div>
              <div class="field-body">
                <div class="field is-narrow">
                  <div class="control">
                    <div class="select">
                      <select
                        v-model="restoreAtOriginalLocation"
                        :disabled="!canRestoreAtOriginalLocation"
                      >
                        <option :value="false">Custom location</option>
                        <option :value="true">Original location</option>
                      </select>
                    </div>
                  </div>
                  <p
                    v-if="!canRestoreAtOriginalLocation"
                    class="help is-warning"
                  >
                    A custom destination has to be provided because some
                    datafile copies cannot be recovered at their original
                    location
                  </p>
                </div>
              </div>
            </div>
            <div class="field is-horizontal">
              <div class="field-label has-text-left">
                <label class="label">Restore Path</label>
              </div>
              <div class="field-body">
                <div class="field is-expanded">
                  <div class="control">
                    <input
                      :disabled="restoreAtOriginalLocation"
                      class="input"
                      :class="{
                        'is-danger':
                          !restoreAtOriginalLocation && hasValidationError,
                      }"
                      type="text"
                      :placeholder="
                        restoreAtOriginalLocation
                          ? 'Original location'
                          : 'Restore destination path'
                      "
                      :value="
                        restoreAtOriginalLocation ? '-' : restoreDestination
                      "
                      @input="(e) => (restoreDestination = e.target.value)"
                    />
                  </div>
                  <p class="help is-warning">
                    N.B. datafile status will not be turned back ONLINE when
                    restoring to a custom location
                  </p>
                  <div
                    v-if="!restoreAtOriginalLocation && hasValidationError"
                    class="help is-danger"
                    style="display: inline"
                  >
                    <span class="mr-2">Restore path is invalid:</span>
                    <ul style="display: inline">
                      <li
                        v-for="(err, index) of v$.restoreDestination.$errors"
                        :key="`err-${index}`"
                        class="mr-2"
                        style="display: inline-block"
                      >
                        {{ err.$message }}.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DefaultPanel>

        <section class="notifications mt-4 mb-2">
          <div class="notification is-info p-2 mb-2">
            <span class="icon is-small">
              <i class="fa-solid fa-info fa-sm" />
            </span>
            <span class="text is-size-7">
              Restoration is an asynchronous process which can take a while. A
              task will be submitted and you can follow its progress through the
              task list
            </span>
          </div>
        </section>
        <div class="is-flex is-justify-content-flex-end mt-5">
          <button
            class="button is-primary has-text-weight-bold"
            :class="{ 'is-loading': isRestoring }"
            :disabled="
              isRestoring || restorableDatasetsCount === 0 || hasValidationError
            "
            @click="submitRestoreRequest"
          >
            <span class="icon fa-stack hoverable-icon has-text-white mr-2">
              <i class="fa-solid fa-archive stack-bg stack-bottom"></i>
              <i class="fa-solid fa-up-long stack-small stack-fg stack-top"></i>
            </span>
            <span>
              Restore {{ restorableDatasetsCount }}
              {{ pluralize('dataset', restorableDatasetsCount) }}
            </span>
          </button>
        </div>
      </section>
    </template>
    <template #footer>
      <div style="width: 100%; text-align: right">
        <a
          class="button is-outlined is-small"
          :class="{ 'is-disabled': isRestoring }"
          @click="closeModal()"
        >
          Close
        </a>
      </div>
    </template>
  </ModalCard>
</template>

<script>
import { ref, computed, watch, watchEffect } from 'vue'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

import pluralize from 'pluralize'
import { useStore } from '@/store'
import { useToggle, watchOnce } from '@vueuse/core'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, helpers } from '@vuelidate/validators'
import { post } from '@/api'
import { fetchDropboxes } from '@/utils/api/dataManagement'
import { capitalize } from '@/filters/filters'

import DefaultPanel from '@/components/DefaultPanel.vue'

const RESTORABLE_API_PATH =
  'data_management/datasets/restorable_datafilecopies/'
const RESTORE_API_PATH = 'data_management/archives/restore_datafilecopies/'

const ERROR_GET_RESTORABLE = {
  type: 'error',
  message: 'Error fetching restorable datafilecopies',
}
const ERROR_NOTHING_RESTORABLE = {
  name: 'no-restorables',
  message: 'No items are restorable',
}

const MESSAGES = {
  storageAccess: {
    error:
      'User has no access to the storage volume. This prevent the restoration at the original location.',
    success:
      'User has access to the storage volume. Restoration can happen at original location.',
  },
  storageOnline: {
    error:
      'The storage volume is offline. This prevent the restoration at the original location.',
    success:
      'The storage volume is online. Restoration can happen at original location.',
  },
  archiveExpired: {
    error: 'The archive has passed its expiry date. Recovery not guaranteed',
    success: 'The archive has not yet passed its expiry date',
  },
}

export default {
  name: 'BatchRestoreModal',
  components: {
    DefaultPanel,
    DynamicScroller,
    DynamicScrollerItem,
  },
  props: {
    isOpened: {
      type: Boolean,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    records: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const [showRestorable, toggleRestorable] = useToggle(false)
    const [showNonRestorable, toggleNonRestorable] = useToggle(false)
    const store = useStore()
    const loggedInUserGroupIds = computed(() => {
      return new Set(store.getters['auth/loggedInUserGroupIds'])
    })
    const isOpened = computed(() => props.isOpened)
    const closeModal = () => emit('update:is-opened', false)
    const isLoadingRestorable = ref(false)
    const isRestoring = ref(false)
    const rawDatafilesInfo = ref([])
    const rawDatafilesInfo2 = ref([])
    const isArchiveModel = computed(() => props.modelName === 'archives')
    const ids = computed(() => props.records.map((item) => item.id.value))

    async function getRestorableData() {
      reset()
      restorableDatasets.value = []
      nonRestorableDatasets.value = []
      isLoadingRestorable.value = true
      let payloadKey = isArchiveModel.value ? 'archive_id' : 'dataset_ids'
      let payloadVal = isArchiveModel.value ? ids.value[0] : ids.value
      const payload = { [payloadKey]: payloadVal }

      try {
        const response = await post(RESTORABLE_API_PATH, { data: payload })
        rawDatafilesInfo.value = response.data
        rawDatafilesInfo2.value = response.data.flat()
      } catch (error) {
        store.commit('notify', ERROR_GET_RESTORABLE)
        closeModal()
      } finally {
        isLoadingRestorable.value = false
      }
    }

    const datasetDatafiles = ref({})
    const datafilesData = ref({})
    watch(rawDatafilesInfo2, (rawData) => {
      datafilesData.value = rawData.reduce((datafilesData, datafileInfo) => {
        const datafileId = datafileInfo.datafile.id
        const archivedCopyId = datafileInfo.archived_datafilecopy?.id
        const volumeAccess = datafileInfo.user_has_storagevolume_access
        const volumeOnline = datafileInfo.does_storagevolume_exist
        const isArchived = archivedCopyId !== null
        const isExpired = datafileInfo.is_expired
        const isRestorable = datafileInfo.restorable.is_restorable
        const restorabilityMsg = datafileInfo.restorable.message
        datafilesData[datafileId] = {
          ...datafileInfo,
          volumeAccess,
          volumeOnline,
          isArchived,
          isExpired,
          isRestorable,
          restorabilityMsg,
        }
        return datafilesData
      }, {})
      datasetDatafiles.value = Object.entries(datafilesData.value).reduce(
        (datasetDatafiles, [datafileId, { dataset }]) => {
          if (!datasetDatafiles[dataset.id]) datasetDatafiles[dataset.id] = []
          datasetDatafiles[dataset.id].push(datafileId)
          return datasetDatafiles
        },
        {},
      )
    })

    const restorableDatasets = ref([])
    const nonRestorableDatasets = ref([])
    watch(datasetDatafiles, (data) => {
      Object.entries(data).forEach(([datasetId, datafileIds]) => {
        const dfs = datafileIds.map((id) => datafilesData.value[id])
        const datasetName = dfs[0].dataset.name
        const archivedDfcIds = dfs.map(
          ({ archived_datafilecopy }) => archived_datafilecopy.id,
        )
        const dataset = {
          datafileIds,
          // datasetId
          id: datasetId,
          name: datasetName,
          // datafiles: dfs,
          archivedDfcIds,
          volumeAccess: dfs.map((f) => f.volumeAccess).every(Boolean),
          volumeOnline: dfs.map((f) => f.volumeOnline).every(Boolean),
          isArchived: dfs.map((f) => f.isArchived).every(Boolean),
          isExpired: dfs.map((f) => f.isExpired).every(Boolean),
          isRestorable: dfs.map((f) => f.isRestorable).every(Boolean),
          restorabilityHTMLMsg: [
            '<ul>',
            dfs
              .map(
                ({ datafile, restorabilityMsg }) =>
                  `<li><code class="has-text-danger">${restorabilityMsg}` +
                  `</code> (${datafile.name})</li>`,
              )
              .join(''),
            '</ul>',
          ].join(''),
          showDetails: false,
        }
        if (dataset.isRestorable) restorableDatasets.value.push(dataset)
        else nonRestorableDatasets.value.push(dataset)
      })
      restorableDatasets.value = restorableDatasets.value.sort((a, b) =>
        a.name.localeCompare(b.name),
      )
      nonRestorableDatasets.value = nonRestorableDatasets.value.sort((a, b) =>
        a.name.localeCompare(b.name),
      )
    })

    const datafilesGroupIds = computed(() => {
      return new Set(
        Object.values(datafilesData.value).map((d) => d.owned_by.id),
      )
    })

    const datafilesBelongToUserGroup = computed(() => {
      return Array.from(datafilesGroupIds.value).every((id) =>
        loggedInUserGroupIds.value.has(id),
      )
    })

    const datasetCount = computed(() => {
      return (
        restorableDatasets.value.length + nonRestorableDatasets.value.length
      )
    })
    const restorableDatasetsCount = computed(() => {
      return restorableDatasets.value.length
    })
    const nonRestorableDatasetsCount = computed(() => {
      return nonRestorableDatasets.value.length
    })

    const someVolumesOffline = computed(() => {
      let someOffline = false
      for (let d of restorableDatasets.value) {
        if (!d.volumeOnline) {
          someOffline = true
          break
        }
      }
      return someOffline
    })
    const someVolumeAccessDenied = computed(() => {
      let someDenied = false
      for (let d of restorableDatasets.value) {
        if (!d.volumeAccess) {
          someDenied = true
          break
        }
      }
      return someDenied
    })
    const someArchiveExpired = computed(() => {
      let someExpired = false
      for (let d of restorableDatasets.value) {
        if (d.isExpired) {
          someExpired = true
          break
        }
      }
      return someExpired
    })

    const showListNumbers = computed(
      () =>
        [
          someVolumesOffline.value,
          someVolumeAccessDenied.value,
          !datafilesBelongToUserGroup.value,
        ].filter((v) => v).length > 1,
    )

    const restorabilityInfo = computed(() => [
      {
        id: 'storage-online',
        message: someVolumesOffline.value
          ? 'Some storage volumes are offline, which prevents restoration to original location'
          : 'Storage volumes are online',
        color: someVolumesOffline.value ? 'warning' : 'success',
      },
      {
        id: 'storage-access',
        message: someVolumeAccessDenied.value
          ? 'Some storage volumes are inaccessible, which prevents restoration to original location'
          : 'Storage volumes are accessible',
        color: someVolumeAccessDenied.value ? 'warning' : 'success',
      },
      {
        id: 'archive-expiry',
        message: someArchiveExpired.value
          ? 'Some archives have expired, which may prevent restoration'
          : 'All archives are valid',
        color: someArchiveExpired.value ? 'warning' : 'success',
      },
    ])

    const canRestoreAtOriginalLocation = computed(() => {
      let canDo = datafilesBelongToUserGroup.value
      if (datafilesBelongToUserGroup.value) {
        for (let d of restorableDatasets.value) {
          if (!d.volumeOnline || !d.volumeAccess) {
            canDo = false
            break
          }
        }
      }
      return canDo
    })

    const errors = ref({})
    watchEffect(() => {
      errors.value = {}
      if (!isLoadingRestorable.value && rawDatafilesInfo.value.length === 0) {
        errors.value[ERROR_NOTHING_RESTORABLE.name] =
          ERROR_NOTHING_RESTORABLE.message
      }
    })

    /* Form */
    const restoreAtOriginalLocation = ref(canRestoreAtOriginalLocation.value)
    watchOnce(canRestoreAtOriginalLocation, (canDo) => {
      restoreAtOriginalLocation.value = canDo
    })

    const dropboxes = ref([])
    const defaultRestoreDest = computed(
      () => dropboxes.value.find((d) => d.is_user_dropbox)?.path,
    )

    /* Validation */
    const restoreDestination = ref()
    /* defaultRestoreDest is obtained asynchronously, need a watcher to init
     * restoreDestination */
    watch(defaultRestoreDest, (val) => (restoreDestination.value = val))

    const rules = computed(() => ({
      restoreDestination: {
        ...(!restoreAtOriginalLocation.value && {
          required,
          minLength: minLength(3),
        }),
        isRooted: helpers.withMessage(
          'Value must start with /',
          helpers.regex(/^\/.*/),
        ),
        isUnixPath: helpers.withMessage(
          'Value must be a valid UNIX path',
          helpers.regex(/^(\/[a-zA-Z0-9_-]+)+$/),
        ),
      },
    }))
    const v$ = useVuelidate(rules, { restoreDestination }, { $autoDirty: true })
    const hasValidationError = computed(() => v$.value.$errors.length > 0)

    /* Payload */
    const payload = computed(() => ({
      datafilecopies: Object.values(restorableDatasets.value)
        .map(({ archivedDfcIds }) => archivedDfcIds)
        .flat(),
      restore_to: restoreAtOriginalLocation.value
        ? null
        : restoreDestination.value,
    }))

    const submitRestoreRequest = async () => {
      isRestoring.value = true
      try {
        const response = await post(RESTORE_API_PATH, { data: payload.value })
        const responseMsg = `${response.data.message}. `
        if (response.status >= 200) {
          const message =
            responseMsg +
            'Check your task list to follow the restoration progress.'
          store.commit('notify', { type: 'success', message })
        } else {
          store.commit('notify', { type: 'error', responseMsg })
        }
        closeModal()
      } catch (error) {
        let message =
          error?.response?.data?.message ||
          'Error requesting restore-datafilecopies'
        store.commit('notify', { type: 'error', message })
      } finally {
        isRestoring.value = false
      }
    }

    const reset = () => {
      rawDatafilesInfo.value = []
      rawDatafilesInfo2.value = []
      restorableDatasets.value = []
      nonRestorableDatasets.value = []
      restoreDestination.value = ''
      restoreAtOriginalLocation.value = canRestoreAtOriginalLocation.value
      errors.value = {}
    }

    watch(
      isOpened,
      async (opened) => {
        if (opened) {
          getRestorableData()
          dropboxes.value = await fetchDropboxes()
        } else reset()
      },
      { immediate: true },
    )

    return {
      MESSAGES,
      rawDatafilesInfo,
      datasetDatafiles,
      datafilesData,
      restorableDatasets,
      nonRestorableDatasets,
      //
      isArchiveModel,
      isLoadingRestorable,
      isRestoring,
      restorableDatasetsCount,
      nonRestorableDatasetsCount,
      restorabilityInfo,
      someVolumesOffline,
      someVolumeAccessDenied,
      someArchiveExpired,
      loggedInUserGroupIds,
      datafilesGroupIds,
      datafilesBelongToUserGroup,
      canRestoreAtOriginalLocation,
      showListNumbers,
      showRestorable,
      toggleRestorable,
      showNonRestorable,
      toggleNonRestorable,
      datasetCount,
      closeModal,
      errors,
      // Form
      dropboxes,
      defaultRestoreDest,
      restoreDestination,
      restoreAtOriginalLocation,
      // Validation
      v$,
      hasValidationError,
      // Payload
      payload,
      submitRestoreRequest,
    }
  },
  methods: {
    capitalize,
    pluralize,
  },
}
</script>

<style lang="scss" scoped>
header {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  .notifications .notification {
    margin-bottom: 0.2rem;
  }
  .section:empty,
  &:empty {
    display: none;
  }
}

.box {
  border: 1px solid var(--color-border-default);
  padding: 0;
}

.list-items-wrapper {
  a,
  a > label {
    display: flex;
    align-items: center;
    margin: 0;
  }

  .scroller {
    display: block;
    min-height: 40px;
    height: auto;
    max-height: 33vh;
    resize: vertical;
    padding-bottom: 0.7rem;

    .dynam-item {
      a {
        display: flex;
        flex-grow: 1;
        align-items: center;
        justify-content: space-between;
      }
      .detail-icon {
        padding: 0.5rem;
      }
    }

    :deep(.vue-recycle-scroller__item-view) {
      border-bottom: 1px solid var(--color-border-default);
      background-color: var(--color-background-gray-light);
      .even {
        background-color: var(--white);
      }
    }
  }
  ul.item-details {
    li.detail {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      .item-name {
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 1;
        flex-grow: 1;
        min-width: 1px;
        max-width: max-content;
        flex-basis: 33%;
      }

      .item-error-msg {
        display: flex;
        align-items: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex-shrink: 1;
        flex-grow: 4;
        flex-basis: 66%;
        min-width: 300px;

        code {
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
}

ol.restore-loc-list {
  display: inline;
  &:after {
    content: '.';
  }
  li {
    display: inline;
    &:not(:last-of-type) {
      margin-right: 1rem;
    }
  }
  .show-numbers {
    counter-reset: LIST-ITEMS;
    li {
      &:before {
        content: counter(LIST-ITEMS) '.';
        counter-increment: LIST-ITEMS;
        padding-right: 0.25em;
        font-style: italic;
        font-weight: bold;
      }
    }
  }
}
.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
</style>
